import axios from 'axios';

/**
 * Get API base URL with runtime override support.
 *
 * Priority:
 * 1. Runtime config (window.__ENV__.VITE_API_BASE_URL) - set by docker-entrypoint.sh
 * 2. Build-time config (import.meta.env.VITE_API_BASE_URL) - set during npm run build
 * 3. Empty string - uses relative URLs (works with Vite dev proxy)
 */
function getApiBaseUrl(): string {
  // Runtime config from env-config.js (injected at container startup)
  const runtimeUrl = (window as Window & { __ENV__?: { VITE_API_BASE_URL?: string } }).__ENV__
    ?.VITE_API_BASE_URL;
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Build-time config from Vite
  return import.meta.env.VITE_API_BASE_URL || '';
}

const API_BASE_URL = getApiBaseUrl();

/** Storage key for auth token */
const AUTH_TOKEN_KEY = 'auth_token';

/**
 * Shared Axios instance used by the API modules in this directory.
 *
 * Configured with the base URL resolved by `getApiBaseUrl` (runtime
 * `window.__ENV__` override, then the Vite build-time variable, otherwise a
 * relative URL), a default JSON `Content-Type` header, and a 60-second
 * timeout for batch operations.
 *
 * The request interceptor attaches an `Authorization: Bearer` header from the
 * `auth_token` value in localStorage when one is present. The response
 * interceptor handles a 401 by attempting a single silent token refresh via
 * cookie exchange and retrying the original request once; if the refresh
 * fails it clears the stored token and redirects to `/login`.
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second timeout for batch operations
});

// Request interceptor for auth headers and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token to requests if available
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Dev logging
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and 401 redirect
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Handle 401 Unauthorized - attempt silent token refresh before redirecting
      if (error.response.status === 401) {
        // Prevent infinite retry loops
        if (error.config.__isRetry) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Try cookie exchange to get a fresh token
        try {
          const { fetchAuthMe, setAuthToken } = await import('@/api/auth');
          const { token } = await fetchAuthMe();
          setAuthToken(token);
          // Retry the original request with the new token
          error.config.__isRetry = true;
          error.config.headers.Authorization = `Bearer ${token}`;
          return axios.request(error.config);
        } catch {
          // Cookie exchange failed — truly unauthenticated
          localStorage.removeItem(AUTH_TOKEN_KEY);
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      // Server responded with error status
      const message = error.response.data?.detail || error.response.data?.message || 'An error occurred';
      console.error(`[API Error] ${error.response.status}: ${message}`);
    } else if (error.request) {
      // Request made but no response
      console.error('[API Error] No response received');
    } else {
      console.error('[API Error]', error.message);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
